// SkipModal — "無法完成？" link target.
//
// Spec: docs/specs/location-tasks.md §8.7 (跳過機制).
//
// Visible only when task.allow_skip=1. Officer picks a preset reason or
// types a custom one; on confirm we POST /v1/patrols/submissions/:id/skip
// (with header `x-skip-reason` body `{ reason }`) and the parent decides
// whether to advance to the next submission or close the wizard.

import React, { useState } from "react";
import { X } from "lucide-react";

const PRESETS = [
  "設備異常無法拍照",
  "現場無網路",
  "任務需求現場無法執行",
] as const;

interface Props {
  open: boolean;
  taskName: string;
  onCancel: () => void;
  onConfirm: (reason: string) => Promise<void> | void;
  busy?: boolean;
}

export default function SkipModal({ open, taskName, onCancel, onConfirm, busy }: Props): React.ReactElement | null {
  const [picked, setPicked] = useState<string | null>(null);
  const [other, setOther] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const reason = picked === "__other__" ? other.trim() : (picked ?? "");
  const canSubmit = reason.length > 0 && !busy;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" role="dialog" aria-modal="true">
      <div className="w-full sm:max-w-md bg-bg-surface rounded-t-2xl sm:rounded-2xl border border-border p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold">為什麼跳過？</h3>
          <button onClick={onCancel} className="text-text-muted hover:text-text-primary p-1">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-text-muted">任務：{taskName}</p>
        <div className="space-y-2">
          {PRESETS.map((p) => (
            <label key={p} className={`flex items-center gap-2 rounded-lg border p-2 cursor-pointer ${picked === p ? "border-accent bg-accent/10" : "border-border-subtle"}`}>
              <input
                type="radio"
                name="skip-reason"
                value={p}
                checked={picked === p}
                onChange={() => { setPicked(p); setError(null); }}
                className="accent-accent"
              />
              <span className="text-sm">{p}</span>
            </label>
          ))}
          <label className={`flex items-start gap-2 rounded-lg border p-2 cursor-pointer ${picked === "__other__" ? "border-accent bg-accent/10" : "border-border-subtle"}`}>
            <input
              type="radio"
              name="skip-reason"
              value="__other__"
              checked={picked === "__other__"}
              onChange={() => { setPicked("__other__"); setError(null); }}
              className="accent-accent mt-1"
            />
            <div className="flex-1">
              <span className="text-sm">其他</span>
              {picked === "__other__" && (
                <textarea
                  value={other}
                  onChange={(e) => setOther(e.target.value.slice(0, 500))}
                  placeholder="請說明原因…"
                  className="block mt-1 w-full rounded-lg bg-bg-raised border border-border p-2 text-sm text-text-primary"
                  rows={3}
                  autoFocus
                />
              )}
            </div>
          </label>
        </div>
        {error && <p className="text-xs text-danger">{error}</p>}
        <div className="grid grid-cols-2 gap-2 pt-1">
          <button onClick={onCancel} className="h-11 rounded-xl bg-bg-raised text-text-primary text-sm font-medium" disabled={busy}>
            取消
          </button>
          <button
            onClick={async () => {
              if (!canSubmit) { setError("請選擇或填寫原因"); return; }
              try { await onConfirm(reason); }
              catch (e) { setError((e as Error).message ?? "submit_failed"); }
            }}
            disabled={!canSubmit}
            className="h-11 rounded-xl bg-warning text-text-inverse text-sm font-semibold disabled:bg-bg-raised disabled:text-text-muted"
          >
            {busy ? "送出中…" : "確認跳過"}
          </button>
        </div>
      </div>
    </div>
  );
}
